import type { CustomFile } from '../types';

import collectFileEntries from './collectFileEntries';
import { isDirectoryEntry } from './fileSystemEntry';
import getDragEventEntries from './getDragEventEntries';
import getDragEventFiles from './getDragEventFiles';
import getEventFileInput from './getEventFileInput';
import getOriginalEvent, { type TypeEventWithOriginalEvent } from './getOriginalEvent';
import readFileEntries from './readFileEntries';
import toCustomFile from './toCustomFile';

// ----------------------------------------------------------------------------

export type TypeHandleInputEventLike = TypeEventWithOriginalEvent;

/*
 * Why a drop produced no files: the user declined the confirmation, there was
 * nothing usable in it, or reading it failed.
 */
export type TypeInputEventCancelReason = 'cancelled' | 'no-files' | 'error';

export type TypeHandleInputEventConfig = {
    fnConfirmFiles?: (totalFiles: number, evt: DragEvent) => boolean | Promise<boolean>;
    /*
     * How many files a dropped folder has to hold before the confirmation is worth
     * asking for. Defaults to 1 — any folder with something in it — which is the
     * only safe default for a caller that reports nothing while it walks. One that
     * shows the walk and offers to cancel it wants this well above 1: under the
     * threshold the drop simply goes through.
     */
    confirmThreshold?: number;
    // Files found so far while walking a dropped folder.
    fnScanProgress?: (found: number) => void;
    // Files turned into File objects, once the walk is done.
    fnReadProgress?: (done: number, total: number) => void;
    /*
     * A drop that ends with no files ends silently otherwise — no success, no
     * error — which leaves a caller that started showing progress with nothing
     * telling it to stop. Declining the confirmation on a large folder is the
     * common way to get here.
     */
    fnCancel?: (reason: TypeInputEventCancelReason) => void;
    /*
     * Stops the walk and the read where they are and reports 'cancelled'; nothing
     * partial is handed back. One signal per event — see eventListener, which is
     * what resolves the caller's fnSignal into this.
     */
    signal?: AbortSignal;
};

// ----------------------------------------------------------------------------

async function handleInputEvent(
    evt: TypeHandleInputEventLike,
    config: TypeHandleInputEventConfig = {},
): Promise<CustomFile[]> {
    const originalEvent = getOriginalEvent(evt);

    // If it's a drag event
    if (originalEvent instanceof DragEvent) {
        const entries = getDragEventEntries(originalEvent);

        if (entries.length > 0) {
            const hasDirectory = entries.some((entry): any => isDirectoryEntry(entry));

            /*
             * One walk, not two: the count the confirmation needs and the entries
             * the read needs come out of the same pass. Counting separately walked
             * the whole tree a second time, so a folder large enough to be worth
             * confirming was also large enough for the user to wait through it twice
             * — with no feedback either time, since the walk reported nothing.
             */
            const signal = config.signal;
            /*
             * Read through a call, not the property: `aborted` flips while these
             * phases run, which narrowing would not allow for.
             */
            const isAborted = (): boolean => signal?.aborted === true;

            const collected = await collectFileEntries(entries, { fnProgress: config.fnScanProgress, signal });

            if (isAborted()) {
                config.fnCancel?.('cancelled');
                return [];
            }

            const shouldContinue = hasDirectory ? await confirmFiles(collected.length, originalEvent, config) : true;

            if (!shouldContinue) {
                config.fnCancel?.('cancelled');
                return [];
            }

            if (collected.length === 0) {
                config.fnCancel?.('no-files');
                return [];
            }

            const files = await readFileEntries(collected, { fnProgress: config.fnReadProgress, signal });

            if (isAborted()) {
                config.fnCancel?.('cancelled');
                return [];
            }

            /*
             * Every entry failed to read. readFileEntries drops the unreadable ones
             * rather than failing the whole drop, so a tree that went away between
             * the drop and the read arrives here empty — and would otherwise end the
             * drop without saying anything.
             */
            if (files.length === 0) {
                config.fnCancel?.('no-files');
            }

            return files;
        }

        const dragFiles = getDragEventFiles(originalEvent);

        if (dragFiles.length === 0) {
            console.warn('DataTransfer does not contain files.');
            config.fnCancel?.('no-files');
            return [];
        }

        return dragFiles;
    }
    // If it's a event
    if (originalEvent instanceof Event) {
        const input = getEventFileInput(originalEvent);

        /*
         * An empty FileList is still truthy, so the count is what says the picker
         * came back with nothing.
         */
        if (input?.files == null || input.files.length === 0) {
            console.warn('No files selected.');
            config.fnCancel?.('no-files');
            return [];
        }

        return Array.from(input.files, (file): any => toCustomFile(file));
    }

    // Unknown
    console.warn('Unknown event:', evt);
    config.fnCancel?.('no-files');

    return [];
}

// ----------------------------------------------------------------------------

async function confirmFiles(totalFiles: number, evt: DragEvent, config: TypeHandleInputEventConfig): Promise<boolean> {
    if (totalFiles <= 0) {
        return true;
    }

    if (totalFiles < Math.max(1, Math.floor(config.confirmThreshold ?? 1))) {
        return true;
    }

    if (typeof config.fnConfirmFiles === 'function') {
        return config.fnConfirmFiles(totalFiles, evt);
    }

    return true;
}

// ----------------------------------------------------------------------------

export default handleInputEvent;
